import classNames from 'classnames';

import useElementSize from '../../hooks/useElementSize';

import BasicButton from '../buttons/BasicButton';
import DocumentsList from '../lists/DocumentsList';
import DocumentImage from '../partials/DocumentImage';
import DocumentFeaturedCard from './DocumentFeaturedCard';

import styles from '../../styles/cards/pro-onboarding-card.module.css';

interface PlaceholderCardProps {
    id: string;
    image: Image | null;
    selectedPopularDocument: DocumentSnippet | null;
    selectDocument: (id: string) => void;
}

function PlaceholderCard({
    id,
    image,
    selectedPopularDocument,
    selectDocument,
}: PlaceholderCardProps) {
    const { id: selectedId = null } = selectedPopularDocument || {};
    return (
        <BasicButton
            className={classNames([
                styles.placeholderCard,
                {
                    [styles.selected]: id === selectedId,
                },
            ])}
            onClick={() => {
                selectDocument(id);
            }}
        >
            <DocumentImage media={image} className={styles.image} />
            <div className={styles.label}>
                <div className={styles.placeholderLine} />
                <div className={styles.placeholderLine} />
                <div className={classNames([styles.placeholderLine, styles.author])} />
            </div>
        </BasicButton>
    );
}

interface ProOnboardingCardProps {
    id?: string | null;
    title?: string | null;
    description?: string | null;
    type?: string | null;
    popularDocuments?: DocumentSnippet[] | null;
    selectedPopularDocument?: DocumentSnippet | null;
    selectDocument?: (id: string) => void;
    className?: string | null;
}

function ProOnboardingCard({
    id = null,
    title = null,
    description = null,
    type = null,
    popularDocuments = null,
    selectedPopularDocument = null,
    selectDocument = null,
    className = null,
}: ProOnboardingCardProps) {
    const {
        ref: cardSizeRef,
        width: cardWidth = null,
        height: cardHeight = null,
    } = useElementSize();
    const { ref: pageSizeRef, width: pageWidth = null } = useElementSize();
    const scale = cardWidth !== null && pageWidth !== null ? Math.min(1, pageWidth / cardWidth) : 1;
    const { image = null } = selectedPopularDocument || {};
    return (
        <div className={classNames([styles.container, styles[type], className])}>
            <div className={styles.inner}>
                <div className={styles.content}>
                    {title !== null ? (
                        <h4
                            className={styles.title}
                            dangerouslySetInnerHTML={{ __html: title || '' }}
                        />
                    ) : null}
                    {description !== null ? (
                        <div
                            className={styles.description}
                            dangerouslySetInnerHTML={{ __html: description || '' }}
                        />
                    ) : null}
                </div>
                {id === 'step-list' ? (
                    <DocumentsList
                        items={popularDocuments}
                        withoutLoader
                        count={12}
                        presentation="grid"
                        columns={3}
                        cardProps={{
                            selectedPopularDocument,
                            selectDocument,
                        }}
                        cardComponent={PlaceholderCard}
                        className={styles.list}
                        itemClassName={styles.item}
                    />
                ) : null}
                {id === 'step-page' ? (
                    <div className={styles.placeholderPage}>
                        <div className={styles.shareButton} />
                        <DocumentImage media={image} className={styles.image} />
                        <div className={styles.header}>
                            <div className={styles.placeholderLine} />
                            <div className={styles.placeholderLine} />
                            <div className={classNames([styles.placeholderLine, styles.author])} />
                        </div>
                        <div className={styles.body}>
                            <div className={styles.placeholderLine} />
                            <div className={styles.placeholderLine} />
                            <div className={styles.placeholderLine} />
                            <div className={styles.placeholderLine} />
                        </div>
                    </div>
                ) : null}
                {id === 'step-link' || id === 'step-share' ? (
                    <div
                        className={classNames([
                            styles.placeholderPage,
                            {
                                [styles.dark]: id === 'step-share',
                            },
                        ])}
                    >
                        {id === 'step-link' ? (
                            <div className={styles.linkImage} />
                        ) : (
                            <div className={styles.socialImage} />
                        )}
                        <div className={styles.cardContainer} ref={pageSizeRef}>
                            <div
                                className={styles.cardScale}
                                style={{
                                    width: cardWidth !== null ? cardWidth * scale : 'auto',
                                    height: cardHeight !== null ? cardHeight * scale : 'auto',
                                }}
                            >
                                <div
                                    className={styles.cardSize}
                                    style={{
                                        transform: `scale(${scale})`,
                                    }}
                                    ref={cardSizeRef}
                                >
                                    <DocumentFeaturedCard
                                        {...selectedPopularDocument}
                                        withUrbaniaLogo
                                        withoutSurtitle
                                        withoutDate
                                        withoutAuthorBox
                                        theme="over-bottom-left"
                                        imageSize="large"
                                        className={styles.featuredCard}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export default ProOnboardingCard;
